import { parseCallbackState, consumeStoredNonce } from '@/modules/User/services/oidcAuth'
import { apiPostSsoCallback } from '@/modules/User/services/authentication'
import { apiGetDamage } from '@/modules/Damage/services/damages'
import userLoginMixin from '@/modules/User/mixins/userLoginMixin'
import flowMixin from '@/modules/Flow/mixins/flowMixin'
import maSpinner from '@/components/spinner'
import { mapMutations, mapActions } from 'vuex'
import { WARRANTY_MODULE_NAME, WARRANTY_UPDATE_SINISTER_SUMMARY, WARRANTY_UPDATE_FORMID, WARRANTY_UPDATE_SINID } from '@/store/types'
import { prepareErrorForSentry } from '@/utils/errorHandling'
import { errorsTrack } from '@/utils/registeredErrors'

export default {
  name: 'callback',
  mixins: [userLoginMixin, flowMixin],
  components: {
    maSpinner
  },
  methods: {
    ...mapMutations(WARRANTY_MODULE_NAME, {
      mutWarrantyUpdateSinisterSummary: WARRANTY_UPDATE_SINISTER_SUMMARY
    }),

    ...mapActions(WARRANTY_MODULE_NAME, {
      updateWarrantyId: WARRANTY_UPDATE_FORMID,
      updateSinisterId: WARRANTY_UPDATE_SINID
    }),
  },
  async mounted() {
    let statePayload = null

    try {
      const { code, state, error: oidcError } = this.$route.query
      if (oidcError) throw new Error(`OIDC provider returned error: ${oidcError}`)
      if (!code || !state) throw new Error('Missing code/state in OIDC callback')

      statePayload = parseCallbackState(state)
      const expectedNonce = consumeStoredNonce()
      if (!statePayload || !expectedNonce || statePayload.nonce !== expectedNonce) {
        throw new Error('Invalid or expired OIDC state')
      }

      const sinisterId = statePayload.sinisterId
      if (!sinisterId) throw new Error('sinisterId missing from OIDC state')

      const redirectUri = `${window.location.origin}/callback`
      const sessionData = await apiPostSsoCallback(sinisterId, code, redirectUri)
      if (!sessionData || !sessionData.token) throw new Error('SSO callback returned no session token')

      sessionStorage.setItem('mavAuth', `Token ${sessionData.token}`)
      sessionStorage.setItem('sinId', sessionData.sinisterId)
      sessionStorage.setItem('formId', sessionData.formId)
      sessionStorage.setItem('renewToken', 'false')

      this.updateWarrantyId(sessionData.formId)
      this.updateSinisterId(sessionData.sinisterId)

      await this.getUserData()
      await apiGetDamage(this.getSinisterId).then((damages) => {
        this.mutWarrantyUpdateSinisterSummary(damages)
      })

      sessionStorage.setItem('user', 'scuser')

      this.$router.push({
        name: 'claim-home',
        query: { sinisterId, user_type: statePayload.userType, lang: statePayload.lang }
      }).catch(() => {})
    } catch (e) {
      const errorStr = `${e.message} on callback.mounted`
      if (!errorsTrack.includes(errorStr)) errorsTrack.push(errorStr)

      const context = { component: 'callback', method: 'mounted', route: this.$route.path }
      const tags = prepareErrorForSentry(e, context, { willRedirect: true })
      const sinisterId = statePayload && statePayload.sinisterId
      const severity = e.response && e.response.status >= 500 ? 'error' : 'warning'
      this.sendSentryError(errorStr, severity, tags, sinisterId, 'OidcCallbackFailed')

      // App.js's checkSinisterData() (which normally sends the OTP code) never runs for
      // the SSO path, and this is a client-side route change, not a full page load, so
      // nothing else will trigger it either - send it ourselves before landing on /otp.
      if (sinisterId) {
        await this.getOtpStatus(sinisterId)
      }

      const fallbackQuery = statePayload
        ? { sinisterId: statePayload.sinisterId, user_type: statePayload.userType, lang: statePayload.lang }
        : { ...this.$route.query }
      this.$router.push({ name: 'otp', query: fallbackQuery }).catch(() => {})
    }
  }
}
